// ═══════════════════════════════════════════════════════════════════
// CapexPage — المصاريف الرأسمالية (v3.3.391)
// مشاريع رأسمالية مستقلة + مصاريف شهرية (ماضية + متوقعة)
// تظهر في كشف التدفقات النقدية تحت Cash-out → Assets كعناصر تلقائية
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo } from "react";
import { capex } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { ComposedChart, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from "recharts";
import { FiPackage, FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiCheck, FiCalendar, FiTrendingUp, FiPieChart, FiDownload, FiUpload, FiFile } from "react-icons/fi";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
// v3.3.439 — Format date as dd/mm/yyyy
const fmtDateDMY = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};
const ARABIC_MONTHS_SHORT = ["ينا","فبر","مار","أبر","ماي","يون","يول","أغس","سبت","أكت","نوف","ديس"];
const monthLabel = (key) => {
  const [y, m] = String(key).split("-");
  return `${ARABIC_MONTHS_SHORT[parseInt(m) - 1] || m} ${y.slice(2)}`;
};

// ─── Page Header ───
function PageHeaderCapex({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {icon}
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{title}</h1>
      </div>
      {subtitle && <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

export default function CapexPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role === "admin" && !user?.impersonating) || user?.is_admin;
  // v3.3.432 — Granular CapEx permissions (with backward-compat fallback)
  const canEdit = isAdmin || user?.capex_can_add || user?.capex_can_edit || user?.capex_can_delete || user?.can_edit_projects;
  const canAdd = isAdmin || user?.capex_can_add || user?.can_edit_projects;
  const canDelete = isAdmin || user?.capex_can_delete || user?.can_edit_projects;
  const canExport = isAdmin || user?.capex_can_export || user?.can_view_projects;

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null); // null = "all"
  const [expenses, setExpenses] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [showImport, setShowImport] = useState(false); // v3.3.394

  const loadProjects = async () => {
    try {
      const r = await capex.listProjects();
      setProjects(r.data.projects || []);
    } catch (e) {
      console.error(e);
      toast.error("فشل تحميل المشاريع");
    }
  };

  const loadAnalysis = async (pid) => {
    setLoading(true);
    try {
      const r = await capex.analyze(pid);
      setAnalysis(r.data);
    } catch (e) {
      console.error(e);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async (pid) => {
    if (!pid) { setExpenses([]); return; }
    try {
      const r = await capex.listExpenses(pid);
      setExpenses(r.data.expenses || []);
    } catch (e) {
      console.error(e);
      setExpenses([]);
    }
  };

  useEffect(() => { loadProjects(); }, []);
  useEffect(() => {
    loadAnalysis(selectedProjectId);
    loadExpenses(selectedProjectId);
  }, [selectedProjectId]);

  return (
    <div style={{ padding: "16px 20px", direction: "rtl" }}>
      <PageHeaderCapex
        icon={<FiPackage size={26} style={{ color: "#9333ea" }} />}
        title="المصاريف الرأسمالية"
        subtitle="مشاريع رأسمالية مستقلة — مصاريف ماضية ومتوقعة. تظهر تلقائياً في كشف التدفقات النقدية تحت Assets."
      />

      {/* Top bar — project selector */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        marginBottom: 14, padding: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      }}>
        <button
          onClick={() => setSelectedProjectId(null)}
          style={{
            padding: "7px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer",
            border: "1px solid",
            ...(selectedProjectId === null
              ? { background: "#9333ea", color: "#fff", borderColor: "#9333ea" }
              : { background: "#fff", color: "#475569", borderColor: "#cbd5e1" }),
          }}
        >
          📊 كل المشاريع
        </button>
        <select
          value={selectedProjectId || ""}
          onChange={e => setSelectedProjectId(e.target.value ? parseInt(e.target.value) : null)}
          style={{ padding: "7px 12px", fontSize: 13, borderRadius: 7, border: "1px solid #cbd5e1", fontFamily: "inherit", minWidth: 240, cursor: "pointer" }}
        >
          <option value="">— اختر مشروعاً —</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        {canAdd && (
          <button
            onClick={() => setCreatingProject(v => !v)}
            style={{ padding: "7px 14px", background: creatingProject ? "#e2e8f0" : "#16a34a", color: creatingProject ? "#475569" : "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", marginRight: "auto" }}
          >
            {creatingProject ? "إلغاء" : "+ مشروع جديد"}
          </button>
        )}

        {/* v3.3.394 — Export / Import buttons (v3.3.432 — granular permissions) */}
        {selectedProjectId !== null && canExport && (
          <button
            onClick={async () => {
              const proj = projects.find(p => p.id === selectedProjectId);
              try {
                await capex.exportProject(selectedProjectId, proj?.name);
                toast.success("تم التحميل");
              } catch (e) {
                toast.error(e.response?.data?.error || "فشل التصدير");
                console.error("export error:", e);
              }
            }}
            style={{
              padding: "7px 14px", background: "#0891b2", color: "#fff",
              border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700,
              cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
            title="تصدير المشروع كملف Excel"
          >
            <FiDownload size={13} /> تصدير
          </button>
        )}
        {/* v3.3.630 — حُذف الاستيراد اليدوي: البيانات تأتي تلقائياً من الربط بشجرة التكاليف (Daftra) */}
      </div>

      {/* v3.3.394 — Import modal */}
      {showImport && (
        <ImportModal
          projects={projects}
          selectedProjectId={selectedProjectId}
          onClose={() => setShowImport(false)}
          onImported={(newProjectId) => {
            setShowImport(false);
            loadProjects();
            if (newProjectId) setSelectedProjectId(newProjectId);
          }}
        />
      )}

      {/* New project form */}
      {creatingProject && (
        <NewProjectForm
          onCancel={() => setCreatingProject(false)}
          onCreated={() => { setCreatingProject(false); loadProjects(); }}
        />
      )}

      {/* Projects list (visible always) */}
      <ProjectsList
        projects={projects}
        selectedProjectId={selectedProjectId}
        canEdit={canEdit}
        onSelect={(id) => setSelectedProjectId(id)}
        onUpdate={loadProjects}
        editingProjectId={editingProjectId}
        setEditingProjectId={setEditingProjectId}
      />

      {/* v3.3.630 — الربط بشجرة التكاليف المحاسبية في الأعلى (الفعلي من المحاسبة) */}
      {selectedProjectId !== null && (
        <AccountingLinkSection
          key={selectedProjectId}
          projectId={selectedProjectId}
          project={projects.find(p => p.id === selectedProjectId)}
          canEdit={canEdit}
          onLinked={loadProjects}
        />
      )}

      {/* Detail view: expenses table + analysis chart.
          v3.3.629 — يُخفى عند ربط المشروع بشجرة التكاليف (البيانات تأتي من الربط بدل الإدخال/الاستيراد) */}
      {selectedProjectId !== null && !projects.find(p => p.id === selectedProjectId)?.cc_tree_node_id && (
        <ExpensesSection
          projectId={selectedProjectId}
          project={projects.find(p => p.id === selectedProjectId)}
          expenses={expenses}
          canEdit={canEdit}
          onChange={() => { loadExpenses(selectedProjectId); loadAnalysis(selectedProjectId); loadProjects(); }}
        />
      )}

      {/* Analysis chart (always shown when analysis loaded) */}
      <AnalysisChart
        analysis={analysis}
        loading={loading}
        projectName={selectedProjectId ? projects.find(p => p.id === selectedProjectId)?.name : null}
      />

      {/* Per-project breakdown (only in "all projects" view) */}
      {selectedProjectId === null && analysis?.projects?.length > 0 && (
        <ProjectBreakdownTable projects={analysis.projects} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// New Project Form
// ═══════════════════════════════════════════════════════════════════
function NewProjectForm({ onCancel, onCreated }) {
  const [name, setName] = useState("");
  const [budget, setBudget] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");

  const submit = async () => {
    if (!name.trim()) { toast.error("اسم المشروع مطلوب"); return; }
    try {
      await capex.createProject({
        name: name.trim(),
        description: description.trim() || null,
        total_budget: parseFloat(budget) || 0,
        start_date: startDate || null,
        end_date: endDate || null,
      });
      toast.success("تم إنشاء المشروع");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإنشاء");
    }
  };

  return (
    <div style={{ marginBottom: 14, padding: 14, background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 10, color: "#6b21a8" }}>مشروع رأسمالي جديد</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="اسم المشروع *"
          style={{ padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontFamily: "inherit", fontSize: 13 }}
        />
        <input
          type="number"
          value={budget}
          onChange={e => setBudget(e.target.value)}
          placeholder="الميزانية الإجمالية"
          style={{ padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13, textAlign: "right", direction: "ltr" }}
        />
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          placeholder="تاريخ البدء"
          style={{ padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13 }}
        />
        <input
          type="date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          placeholder="تاريخ الانتهاء"
          style={{ padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 13 }}
        />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="الوصف (اختياري)"
          style={{ flex: 1, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 7, fontFamily: "inherit", fontSize: 13 }}
        />
        <button
          onClick={submit}
          style={{ padding: "7px 22px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
        >إنشاء</button>
        <button
          onClick={onCancel}
          style={{ padding: "7px 16px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
        >إلغاء</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Projects List (cards)
// ═══════════════════════════════════════════════════════════════════
function ProjectsList({ projects, selectedProjectId, canEdit, onSelect, onUpdate, editingProjectId, setEditingProjectId }) {
  if (projects.length === 0) {
    return (
      <div style={{ padding: 30, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", textAlign: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#475569", marginBottom: 4 }}>لا توجد مشاريع رأسمالية بعد</div>
        <div style={{ fontSize: 12, color: "#64748b" }}>اضغط "+ مشروع جديد" لإضافة أول مشروع</div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
      {projects.map(p => (
        <ProjectCard
          key={p.id}
          project={p}
          isSelected={selectedProjectId === p.id}
          isEditing={editingProjectId === p.id}
          canEdit={canEdit}
          onSelect={() => onSelect(p.id)}
          onEditToggle={() => setEditingProjectId(editingProjectId === p.id ? null : p.id)}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
}

function ProjectCard({ project, isSelected, isEditing, canEdit, onSelect, onEditToggle, onUpdate }) {
  const [name, setName] = useState(project.name);
  const [budget, setBudget] = useState(project.total_budget);
  const [startDate, setStartDate] = useState(project.start_date || "");
  const [endDate, setEndDate] = useState(project.end_date || "");

  const spent = Number(project.spent_actual) || 0;
  const forecast = Number(project.spent_forecast) || 0;
  const total = Number(project.total_budget) || 0;
  const usedPct = total > 0 ? Math.min(100, (spent / total) * 100) : 0;
  const forecastPct = total > 0 ? Math.min(100, (forecast / total) * 100) : 0;

  const save = async () => {
    try {
      await capex.updateProject(project.id, {
        name: name.trim(),
        total_budget: parseFloat(budget) || 0,
        start_date: startDate || null,
        end_date: endDate || null,
      });
      toast.success("تم الحفظ");
      onEditToggle();
      onUpdate();
    } catch (e) {
      toast.error("فشل الحفظ");
    }
  };

  const remove = async () => {
    if (!window.confirm(`حذف المشروع "${project.name}" وكل مصاريفه؟`)) return;
    try {
      await capex.deleteProject(project.id);
      toast.success("تم الحذف");
      onUpdate();
    } catch (e) { toast.error("فشل الحذف"); }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: isSelected ? "2px solid #9333ea" : "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onClick={(e) => {
        if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
        if (!isEditing) onSelect();
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        {isEditing ? (
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ flex: 1, padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", fontSize: 13, fontWeight: 700 }}
          />
        ) : (
          <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", lineHeight: 1.3 }}>{project.name}</div>
        )}
        {canEdit && (
          <div style={{ display: "flex", gap: 4 }}>
            {isEditing ? (
              <>
                <button onClick={save} title="حفظ" style={{ padding: "4px 6px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>✓</button>
                <button onClick={onEditToggle} title="إلغاء" style={{ padding: "4px 6px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>✕</button>
              </>
            ) : (
              <>
                <button onClick={onEditToggle} title="تعديل" style={{ padding: "4px 6px", background: "#e0e7ff", color: "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>✎</button>
                <button onClick={remove} title="حذف" style={{ padding: "4px 6px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>🗑</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Budget */}
      <div style={{ marginBottom: 6 }}>
        {isEditing ? (
          <input
            type="number"
            value={budget}
            onChange={e => setBudget(e.target.value)}
            placeholder="الميزانية"
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, textAlign: "right", direction: "ltr" }}
          />
        ) : (
          <div style={{ fontSize: 11, color: "#64748b" }}>الميزانية: <span style={{ fontWeight: 700, direction: "ltr", color: "#0f172a" }}>{fmt(total)}</span></div>
        )}
      </div>

      {/* Dates (when editing) */}
      {isEditing && (
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ flex: 1, padding: "3px 6px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11 }} />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ flex: 1, padding: "3px 6px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11 }} />
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: "#dc2626", fontWeight: 700 }}>المنفق: {fmt(spent)}</span>
        <span style={{ color: "#f59e0b", fontWeight: 700 }}>المتوقع: {fmt(forecast)}</span>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ position: "relative", height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden", marginBottom: 4 }}>
          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${usedPct}%`, background: "#dc2626" }} />
          <div style={{ position: "absolute", left: `${usedPct}%`, top: 0, height: "100%", width: `${forecastPct}%`, background: "#f59e0b", opacity: 0.7 }} />
        </div>
      )}
      <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
        {project.expense_count || 0} حركة
        {/* v3.3.439 — Show last activity date */}
        {project.last_expense_date && (
          <>
            {" · "}
            <span style={{ color: "#64748b" }}>
              آخر تحديث: <span style={{ direction: "ltr", display: "inline-block", fontWeight: 600 }}>{fmtDateDMY(project.last_expense_updated_at || project.last_expense_date)}</span>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Category definitions (v3.3.392)
// 4 categories per CAPEX project:
//   - land: قيمة شراء الأرض (single value, not monthly)
//   - structure: تدفقات العظم (monthly schedule)
//   - finishing: تدفقات التشطيب (monthly schedule)
//   - non_construction: المصاريف غير الإنشائية (monthly schedule)
// ═══════════════════════════════════════════════════════════════════
// v3.3.451 — Added 2 new categories: electricity_plumbing + financing_expenses
// v3.3.485 — Added 3 financing sub-categories: program_fee, admin_fee_capex, interest_expense
const CAPEX_CATEGORIES = [
  { key: "land",                 label: "قيمة شراء الأرض",        icon: "🟫", color: "#92400e", bg: "#fef3c7", border: "#fcd34d", isMonthly: false, group: "execution" },
  { key: "structure",            label: "تدفقات العظم",           icon: "🏗️", color: "#7c2d12", bg: "#fee2e2", border: "#fca5a5", isMonthly: true,  group: "execution" },
  { key: "electricity_plumbing", label: "الكهرباء والسباكة",       icon: "⚡", color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd", isMonthly: true,  group: "execution" },
  { key: "finishing",            label: "تدفقات التشطيب",         icon: "🎨", color: "#5b21b6", bg: "#ede9fe", border: "#c4b5fd", isMonthly: true,  group: "execution" },
  { key: "non_construction",     label: "المصاريف غير الإنشائية",  icon: "📋", color: "#075985", bg: "#e0f2fe", border: "#7dd3fc", isMonthly: true,  group: "execution" },
  { key: "dalala",               label: "الدلالات",                icon: "🤝", color: "#9333ea", bg: "#f3e8ff", border: "#d8b4fe", isMonthly: true,  group: "marketing" },
  { key: "marketing_expenses",   label: "المصاريف التسويقية",      icon: "📢", color: "#c2410c", bg: "#ffedd5", border: "#fdba74", isMonthly: true,  group: "marketing" },
  // Financing group (v3.3.485 split into 3 sub-buckets)
  { key: "program_fee",          label: "رسوم البرنامج",           icon: "🌸", color: "#be185d", bg: "#fce7f3", border: "#f9a8d4", isMonthly: true,  group: "financing" },
  { key: "admin_fee_capex",      label: "الرسوم الإدارية",         icon: "🟤", color: "#a16207", bg: "#fef3c7", border: "#fcd34d", isMonthly: true,  group: "financing" },
  { key: "interest_expense",     label: "الفوائد",                 icon: "💰", color: "#9d174d", bg: "#fce7f3", border: "#f9a8d4", isMonthly: true,  group: "financing" },
  // Legacy: keep financing_expenses as a generic bucket too (backward compatibility)
  { key: "financing_expenses",   label: "المصاريف التمويلية الأخرى",icon: "💸", color: "#831843", bg: "#fce7f3", border: "#f9a8d4", isMonthly: true,  group: "financing" },
];

// v3.3.471 — High-level groups for the summary row above the category cards
// تكاليف التنفيذ = جميع البنود ما عدا الدلالات + التسويق + التمويل
// التكاليف التسويقية = الدلالات + المصاريف التسويقية
// التكاليف التمويلية = المصاريف التمويلية
const CAPEX_GROUPS = [
  { key: "execution", label: "تكاليف التنفيذ",     icon: "🏗️", color: "#15803d", bg: "#dcfce7", border: "#86efac" },
  { key: "marketing", label: "التكاليف التسويقية",  icon: "📢", color: "#9333ea", bg: "#f3e8ff", border: "#c4b5fd" },
  { key: "financing", label: "التكاليف التمويلية",  icon: "💸", color: "#9d174d", bg: "#fce7f3", border: "#f9a8d4" },
];

// ═══════════════════════════════════════════════════════════════════
// Expenses Section (organized by 4 fixed categories)
// ═══════════════════════════════════════════════════════════════════
function ExpensesSection({ projectId, project, expenses, canEdit, onChange }) {
  // v3.3.485 — Collapsible state per group (execution/marketing/financing)
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const totals = useMemo(() => {
    const totals = {};
    CAPEX_CATEGORIES.forEach(c => {
      const items = expenses.filter(e => (e.category || "other") === c.key);
      const actual = items.filter(e => !e.is_forecast).reduce((s, e) => s + Number(e.amount), 0);
      const forecast = items.filter(e => e.is_forecast).reduce((s, e) => s + Number(e.amount), 0);
      totals[c.key] = { actual, forecast, total: actual + forecast, count: items.length };
    });
    return totals;
  }, [expenses]);

  const grandTotal = Object.values(totals).reduce((s, t) => s + t.total, 0);

  // v3.3.471 — Compute group totals (execution / marketing / financing)
  const groupTotals = useMemo(() => {
    const out = {};
    CAPEX_GROUPS.forEach(g => {
      const cats = CAPEX_CATEGORIES.filter(c => c.group === g.key);
      const actual = cats.reduce((s, c) => s + (totals[c.key]?.actual || 0), 0);
      const forecast = cats.reduce((s, c) => s + (totals[c.key]?.forecast || 0), 0);
      out[g.key] = { actual, forecast, total: actual + forecast };
    });
    return out;
  }, [totals]);

  // v3.3.484 — Monthly stacked bar chart data (one row per month, one bar per category)
  // Aggregates expenses by year-month and category. Forecast and actual combined.
  const monthlyChartData = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];
    // Group by YYYY-MM
    const buckets = {};
    for (const e of expenses) {
      const dateStr = String(e.expense_date || "").slice(0, 7); // YYYY-MM
      if (!dateStr || dateStr.length !== 7) continue;
      const cat = e.category || "other";
      if (!buckets[dateStr]) {
        buckets[dateStr] = { month: dateStr };
        CAPEX_CATEGORIES.forEach(c => { buckets[dateStr][c.key] = 0; });
        buckets[dateStr]._total = 0;
      }
      if (buckets[dateStr][cat] === undefined) buckets[dateStr][cat] = 0;
      const amt = Number(e.amount) || 0;
      buckets[dateStr][cat] += amt;
      buckets[dateStr]._total += amt;
    }
    // Convert to sorted array, add display label
    const arr = Object.values(buckets).sort((a, b) => a.month.localeCompare(b.month));
    const MONTH_LABELS_AR = ["", "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
    return arr.map(b => {
      const [y, m] = b.month.split("-");
      return { ...b, label: `${MONTH_LABELS_AR[parseInt(m, 10)] || m} ${y.slice(2)}` };
    });
  }, [expenses]);

  // v3.3.484 — Tooltip showing full breakdown when hovering on a bar
  const renderMonthTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload || {};
    // Only show categories with non-zero amounts in this month
    const activeCats = CAPEX_CATEGORIES.filter(c => Number(data[c.key]) > 0);
    return (
      <div style={{
        background: "#fff",
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
        direction: "rtl",
        textAlign: "right",
        fontSize: 11,
        minWidth: 220,
      }}>
        <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #e2e8f0", direction: "ltr", textAlign: "center", fontSize: 12 }}>
          📅 {label}
        </div>
        {activeCats.map(c => (
          <div key={c.key} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", color: c.color, fontWeight: 700 }}>
            <span>{c.icon} {c.label}:</span>
            <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(data[c.key])}</span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0 2px", marginTop: 4, borderTop: "1px solid #e2e8f0", color: "#0f172a", fontWeight: 800, fontSize: 12 }}>
          <span>💎 الإجمالي:</span>
          <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(data._total)}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      {/* v3.3.484 — Monthly stacked bar chart of capex outflows by category */}
      {monthlyChartData.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
              📊 التدفقات الخارجة الشهرية حسب نوع المصروف
            </div>
            <div style={{ fontSize: 10, color: "#64748b" }}>
              {monthlyChartData.length} شهر · مرّر على أي عمود لتفاصيل النوع
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={monthlyChartData} margin={{ top: 5, right: 5, left: 5, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={Math.max(0, Math.floor(monthlyChartData.length / 18))}
                angle={-35}
                textAnchor="end"
                height={55}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v}
              />
              <Tooltip content={renderMonthTooltip} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
              {CAPEX_CATEGORIES.map(c => (
                <Bar
                  key={c.key}
                  dataKey={c.key}
                  stackId="a"
                  fill={c.color}
                  name={`${c.icon} ${c.label}`}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* v3.3.485 — Grand Total banner (full width, dark) */}
      <div style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        borderRadius: 12,
        padding: "14px 18px",
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: "#fff",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.25)",
      }}>
        <div>
          <div style={{ fontSize: 11, color: "#cbd5e1", fontWeight: 700, marginBottom: 3 }}>💎 الإجمالي الكلي للمشروع</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            ميزانية: <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(project?.total_budget || 0)}</span>
            {project?.total_budget > 0 && grandTotal > 0 && (
              <span style={{ marginRight: 8, color: grandTotal > project.total_budget ? "#fca5a5" : "#86efac" }}>
                · المتبقي: <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(project.total_budget - grandTotal)}</span>
              </span>
            )}
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
          {fmt(grandTotal)}
        </div>
      </div>

      {/* v3.3.485 — Three main groups, each as a row: Main card (left/larger) + Sub buckets */}
      {CAPEX_GROUPS.map(g => {
        const gt = groupTotals[g.key];
        const subCats = CAPEX_CATEGORIES.filter(c => c.group === g.key);
        const groupKey = `group:${g.key}`;
        const isExpanded = !collapsedGroups[g.key];
        if (gt.total === 0 && subCats.every(c => totals[c.key]?.total === 0)) {
          // Show but compact for empty groups
        }
        return (
          <div key={g.key} style={{ marginBottom: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 8, alignItems: "stretch" }}>
              {/* Main card (dark color, large total, expand toggle) */}
              <button
                onClick={() => setCollapsedGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                style={{
                  background: g.color,
                  border: `2px solid ${g.color}`,
                  borderRadius: 12,
                  padding: "14px 16px",
                  color: "#fff",
                  cursor: "pointer",
                  textAlign: "right",
                  fontFamily: "inherit",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                  transition: "transform 0.1s",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                title={isExpanded ? "اضغط لطي الفروع" : "اضغط لإظهار الفروع"}
              >
                <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{g.icon} {g.label}</span>
                  <span style={{ fontSize: 14, opacity: 0.85 }}>{isExpanded ? "▼" : "▶"}</span>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
                  {fmt(gt.total)}
                </div>
                {(gt.actual > 0 || gt.forecast > 0) && (
                  <div style={{ fontSize: 10, marginTop: 4, opacity: 0.9, fontWeight: 600 }}>
                    🔴 <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(gt.actual)}</span>
                    {" · "}
                    🟡 <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(gt.forecast)}</span>
                  </div>
                )}
              </button>

              {/* Sub-buckets (lighter colors) */}
              {isExpanded && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`, gap: 6 }}>
                  {subCats.map(c => {
                    const t = totals[c.key] || { total: 0, actual: 0, forecast: 0 };
                    return (
                      <div key={c.key} style={{
                        background: c.bg,
                        border: `1px solid ${c.border}`,
                        borderRadius: 10,
                        padding: "8px 10px",
                        opacity: t.total > 0 ? 1 : 0.5,
                      }}>
                        <div style={{ fontSize: 10, color: c.color, fontWeight: 700, marginBottom: 2 }}>
                          {c.icon} {c.label}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: c.color, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
                          {fmt(t.total)}
                        </div>
                        {(t.actual > 0 || t.forecast > 0) && (
                          <div style={{ fontSize: 9, color: c.color, marginTop: 2, opacity: 0.85 }}>
                            🔴 {fmt(t.actual)} · 🟡 {fmt(t.forecast)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* 4 category sections */}
      {CAPEX_CATEGORIES.map(cat => (
        <CategorySection
          key={cat.key}
          category={cat}
          projectId={projectId}
          expenses={expenses.filter(e => (e.category || "other") === cat.key)}
          canEdit={canEdit}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

// ─── Single Category Section ───
function CategorySection({ category, projectId, expenses, canEdit, onChange }) {
  const [addMode, setAddMode] = useState(null); // null | "single" | "bulk"
  const [collapsed, setCollapsed] = useState(false);

  const total = expenses.reduce((s, e) => s + Number(e.amount), 0);
  const actualTotal = expenses.filter(e => !e.is_forecast).reduce((s, e) => s + Number(e.amount), 0);
  const forecastTotal = expenses.filter(e => e.is_forecast).reduce((s, e) => s + Number(e.amount), 0);

  // Suggest next "دفعة N" index based on existing items
  const nextIndex = useMemo(() => {
    let max = 0;
    for (const e of expenses) {
      const m = String(e.label || "").match(/(\d+)\s*$/);
      if (m) { const n = parseInt(m[1]); if (n > max) max = n; }
    }
    return max + 1;
  }, [expenses]);

  return (
    <div style={{ marginBottom: 14, background: "#fff", border: `1.5px solid ${category.border}`, borderRadius: 10, overflow: "hidden" }}>
      {/* Section header */}
      <div
        style={{
          padding: "10px 14px",
          background: category.bg,
          borderBottom: `1px solid ${category.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setCollapsed(c => !c)}
      >
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: category.color }}>
            {category.icon} {category.label}
            {!category.isMonthly && <span style={{ fontSize: 10, fontWeight: 600, marginRight: 8, opacity: 0.7 }}>· (قيمة واحدة)</span>}
            {category.isMonthly && <span style={{ fontSize: 10, fontWeight: 600, marginRight: 8, opacity: 0.7 }}>· (جدول شهري)</span>}
          </div>
          <div style={{ fontSize: 11, color: category.color, marginTop: 2, opacity: 0.85 }}>
            {expenses.length} حركة | 🔴 {fmt(actualTotal)} | 🟡 {fmt(forecastTotal)}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: category.color, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>
            {fmt(total)}
          </span>
          {/* v3.3.630 — حُذف الإضافة اليدوية (+ مفرد / دفعات متكررة): البيانات تأتي من الربط المحاسبي */}
          <span style={{ color: category.color, fontSize: 14 }}>{collapsed ? "▽" : "△"}</span>
        </div>
      </div>

      {!collapsed && (
        <>
          {addMode === "single" && (
            <NewExpenseForm
              projectId={projectId}
              defaultCategory={category.key}
              isLandCategory={!category.isMonthly}
              onCancel={() => setAddMode(null)}
              onCreated={() => { setAddMode(null); onChange(); }}
            />
          )}

          {addMode === "bulk" && category.isMonthly && (
            <BulkExpensesForm
              projectId={projectId}
              category={category}
              startIndex={nextIndex}
              onCancel={() => setAddMode(null)}
              onCreated={() => { setAddMode(null); onChange(); }}
            />
          )}

          {expenses.length === 0 && !addMode ? (
            <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
              {canEdit ? `لا توجد ${category.isMonthly ? "تدفقات" : "قيمة"} — اضغط + إضافة` : "لا توجد بيانات"}
            </div>
          ) : expenses.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569", fontSize: 11, width: 40 }}>النوع</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569", fontSize: 11, width: 110 }}>التاريخ</th>
                  <th style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, color: "#475569", fontSize: 11 }}>البند / الوصف</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: 11, width: 130 }}>المبلغ</th>
                  {canEdit && <th style={{ padding: "6px 10px", textAlign: "center", fontWeight: 700, color: "#475569", fontSize: 11, width: 70 }}>إجراءات</th>}
                </tr>
              </thead>
              <tbody>
                {expenses.map(e => (
                  <ExpenseRow key={e.id} expense={e} canEdit={canEdit} onChange={onChange} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ─── Bulk Recurring Expenses Form (v3.3.393) ───
function BulkExpensesForm({ projectId, category, startIndex, onCancel, onCreated }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayStr);
  const [count, setCount] = useState(12);
  const [amount, setAmount] = useState("");
  const [labelPrefix, setLabelPrefix] = useState("دفعة");
  const [startIdx, setStartIdx] = useState(startIndex || 1);
  const [isForecast, setIsForecast] = useState(true);

  // Live preview rows
  const preview = useMemo(() => {
    if (!startDate || !count || count < 1) return [];
    const [y, m, d] = startDate.split("-").map(s => parseInt(s));
    const baseDay = d || 1;
    const amt = parseFloat(amount) || 0;
    const rows = [];
    for (let i = 0; i < Math.min(count, 60); i++) {
      const dt = new Date(y, (m - 1) + i, 1);
      const lastDay = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
      const useDay = Math.min(baseDay, lastDay);
      const dateStr = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(useDay).padStart(2, "0")}`;
      rows.push({
        date: dateStr,
        label: `${labelPrefix.trim() || "دفعة"} ${(parseInt(startIdx) || 1) + i}`,
        amount: amt,
      });
    }
    return rows;
  }, [startDate, count, amount, labelPrefix, startIdx]);

  const totalToAdd = preview.reduce((s, r) => s + r.amount, 0);

  const submit = async () => {
    if (!startDate) { toast.error("تاريخ البداية مطلوب"); return; }
    const n = parseInt(count) || 0;
    if (n < 1 || n > 120) { toast.error("عدد الأشهر يجب بين 1 و 120"); return; }
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) { toast.error("المبلغ مطلوب وأكبر من صفر"); return; }
    try {
      const r = await capex.bulkExpenses(projectId, {
        start_date: startDate,
        count: n,
        amount: amt,
        label_prefix: labelPrefix.trim() || "دفعة",
        category: category.key,
        is_forecast: isForecast,
        start_index: parseInt(startIdx) || 1,
      });
      toast.success(`تمت إضافة ${r.data.count} دفعة`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الإضافة");
    }
  };

  return (
    <div style={{ padding: 12, background: "#cffafe", borderBottom: "2px solid #0891b2" }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: "#155e75", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
        🔁 إضافة دفعات شهرية متكررة — {category.label}
      </div>

      {/* Inputs row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr 1.5fr 0.8fr", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>📅 شهر البداية</div>
          <input
            type="month"
            value={startDate.slice(0, 7)}
            onChange={e => setStartDate(`${e.target.value}-01`)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #67e8f9", borderRadius: 6, fontSize: 12, background: "#fff" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>🔢 عدد الأشهر</div>
          <input
            type="number"
            min="1"
            max="120"
            value={count}
            onChange={e => setCount(e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #67e8f9", borderRadius: 6, fontSize: 12, textAlign: "center", background: "#fff" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>💰 المبلغ الشهري</div>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="مثلاً 500000"
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #67e8f9", borderRadius: 6, fontSize: 12, textAlign: "right", direction: "ltr", background: "#fff" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}>🏷️ بادئة الاسم</div>
          <input
            value={labelPrefix}
            onChange={e => setLabelPrefix(e.target.value)}
            placeholder="دفعة / قسط / مرحلة"
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #67e8f9", borderRadius: 6, fontSize: 12, fontFamily: "inherit", background: "#fff" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", marginBottom: 3 }}># يبدأ من</div>
          <input
            type="number"
            min="1"
            value={startIdx}
            onChange={e => setStartIdx(e.target.value)}
            style={{ width: "100%", padding: "6px 8px", border: "1px solid #67e8f9", borderRadius: 6, fontSize: 12, textAlign: "center", background: "#fff" }}
          />
        </div>
      </div>

      {/* Forecast toggle + total */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, padding: "6px 10px", background: "#fff", borderRadius: 6, border: "1px solid #a5f3fc" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: isForecast ? "#92400e" : "#991b1b", cursor: "pointer" }}>
          <input type="checkbox" checked={isForecast} onChange={e => setIsForecast(e.target.checked)} />
          {isForecast ? "🟡 كل الدفعات متوقعة (forecast)" : "🔴 كل الدفعات ماضية (actual)"}
        </label>
        <div style={{ fontSize: 13, color: "#155e75", fontWeight: 800 }}>
          الإجمالي: <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(totalToAdd)}</span>
        </div>
      </div>

      {/* Preview */}
      {preview.length > 0 && parseFloat(amount) > 0 && (
        <div style={{ marginBottom: 10, background: "#fff", border: "1px solid #a5f3fc", borderRadius: 6, overflow: "hidden", maxHeight: 200, overflowY: "auto" }}>
          <div style={{ padding: "5px 10px", background: "#ecfeff", fontSize: 11, fontWeight: 700, color: "#0e7490", borderBottom: "1px solid #a5f3fc", position: "sticky", top: 0 }}>
            👁️ معاينة ({preview.length} دفعة{count > 60 ? ` — معروض أول 60 من ${count}` : ""})
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <tbody>
              {preview.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "4px 10px", width: 40, textAlign: "center" }}>{isForecast ? "🟡" : "🔴"}</td>
                  <td style={{ padding: "4px 10px", color: "#475569", fontVariantNumeric: "tabular-nums", width: 100 }}>{r.date}</td>
                  <td style={{ padding: "4px 10px", color: "#0f172a" }}>{r.label}</td>
                  <td style={{ padding: "4px 10px", textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: isForecast ? "#92400e" : "#dc2626", fontWeight: 700, width: 120 }}>
                    {fmt(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "7px 16px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          إلغاء
        </button>
        <button
          onClick={submit}
          disabled={!amount || parseFloat(amount) <= 0}
          style={{
            padding: "7px 18px",
            background: (!amount || parseFloat(amount) <= 0) ? "#cbd5e1" : "#0891b2",
            color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
            cursor: (!amount || parseFloat(amount) <= 0) ? "not-allowed" : "pointer",
          }}
        >
          ✓ إضافة {count} دفعة بمبلغ {fmt(totalToAdd)}
        </button>
      </div>
    </div>
  );
}

function NewExpenseForm({ projectId, defaultCategory, isLandCategory, onCancel, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [isForecast, setIsForecast] = useState(false);

  const submit = async () => {
    if (!date) { toast.error("التاريخ مطلوب"); return; }
    if (!amount || parseFloat(amount) === 0) { toast.error("المبلغ مطلوب"); return; }
    try {
      await capex.createExpense(projectId, {
        expense_date: date,
        amount: parseFloat(amount) || 0,
        label: label.trim() || null,
        category: defaultCategory,
        is_forecast: isForecast,
      });
      toast.success("تمت الإضافة");
      onCreated();
    } catch (e) {
      toast.error("فشل الإضافة");
    }
  };

  return (
    <div style={{ padding: 12, background: "#eff6ff", borderBottom: "1px solid #bfdbfe", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <input
        type="date"
        value={date}
        onChange={e => setDate(e.target.value)}
        style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12 }}
        title={isLandCategory ? "تاريخ الشراء" : "الشهر/السنة"}
      />
      <input
        value={label}
        onChange={e => setLabel(e.target.value)}
        placeholder={isLandCategory ? "وصف (مثل: قطعة 73 الفيصلية)" : "البند (مثل: دفعة المقاول)"}
        style={{ flex: "1 1 200px", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}
      />
      <input
        type="number"
        value={amount}
        onChange={e => setAmount(e.target.value)}
        placeholder="المبلغ"
        style={{ width: 130, padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, textAlign: "right", direction: "ltr" }}
      />
      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: isForecast ? "#92400e" : "#475569", cursor: "pointer" }}>
        <input type="checkbox" checked={isForecast} onChange={e => setIsForecast(e.target.checked)} />
        🟡 متوقع
      </label>
      <button onClick={submit} style={{ padding: "6px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>إضافة</button>
      <button onClick={onCancel} style={{ padding: "6px 12px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
    </div>
  );
}

function ExpenseRow({ expense, canEdit, onChange }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(expense.expense_date?.slice(0, 10) || "");
  const [amount, setAmount] = useState(expense.amount);
  const [label, setLabel] = useState(expense.label || "");
  const [isForecast, setIsForecast] = useState(expense.is_forecast);

  const save = async () => {
    try {
      await capex.updateExpense(expense.id, {
        expense_date: date,
        amount: parseFloat(amount) || 0,
        label: label.trim() || null,
        is_forecast: isForecast,
      });
      setEditing(false);
      onChange();
      toast.success("تم الحفظ");
    } catch (e) { toast.error("فشل الحفظ"); }
  };

  const remove = async () => {
    if (!window.confirm("حذف هذا البند؟")) return;
    try {
      await capex.deleteExpense(expense.id);
      toast.success("تم الحذف");
      onChange();
    } catch (e) { toast.error("فشل الحذف"); }
  };

  if (editing) {
    return (
      <tr style={{ background: "#fef9c3" }}>
        <td style={{ padding: "6px 8px", textAlign: "center" }}>
          <input type="checkbox" checked={isForecast} onChange={e => setIsForecast(e.target.checked)} title="متوقع" />
        </td>
        <td style={{ padding: "6px 8px" }}>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ width: "100%", padding: "3px 5px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11 }} />
        </td>
        <td style={{ padding: "6px 8px" }}>
          <input value={label} onChange={e => setLabel(e.target.value)} style={{ width: "100%", padding: "3px 5px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11, fontFamily: "inherit" }} />
        </td>
        <td style={{ padding: "6px 8px", textAlign: "left" }}>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} style={{ width: "100%", padding: "3px 5px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11, textAlign: "right", direction: "ltr" }} />
        </td>
        {canEdit && (
          <td style={{ padding: "6px 8px", textAlign: "center" }}>
            <button onClick={save} style={{ padding: "3px 6px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, marginLeft: 2 }}>✓</button>
            <button onClick={() => setEditing(false)} style={{ padding: "3px 6px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>✕</button>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
      <td style={{ padding: "8px 10px", textAlign: "center" }}>
        <span title={expense.is_forecast ? "متوقع" : "ماضي"} style={{ fontSize: 13 }}>
          {expense.is_forecast ? "🟡" : "🔴"}
        </span>
      </td>
      <td style={{ padding: "8px 10px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
        {expense.expense_date?.slice(0, 10)}
      </td>
      <td style={{ padding: "8px 10px", color: "#0f172a" }}>{expense.label || "—"}</td>
      <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", fontWeight: 700, color: expense.is_forecast ? "#92400e" : "#dc2626", fontVariantNumeric: "tabular-nums" }}>
        {fmt(expense.amount)}
      </td>
      {canEdit && (
        <td style={{ padding: "8px 10px", textAlign: "center" }}>
          <button onClick={() => setEditing(true)} style={{ padding: "3px 8px", background: "#e0e7ff", color: "#3730a3", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, marginLeft: 4 }}>✎</button>
          <button onClick={remove} style={{ padding: "3px 8px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700 }}>🗑</button>
        </td>
      )}
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Analysis Chart
// ═══════════════════════════════════════════════════════════════════
// v3.4.689 — colour-by-project palette (fixed order by project rank; forecast = same hue, lighter)
const PROJ_COLORS = ["#dc2626", "#2563eb", "#059669", "#7c3aed", "#ea580c", "#0891b2", "#db2777", "#65a30d", "#4f46e5", "#b45309", "#0d9488", "#9333ea", "#e11d48", "#475569"];
function AnalysisChart({ analysis, loading, projectName }) {
  const [byProject, setByProject] = useState(() => { try { return localStorage.getItem("capex_chart_by_project") === "1"; } catch (_) { return false; } });
  const toggleByProject = () => setByProject((o) => { try { localStorage.setItem("capex_chart_by_project", o ? "0" : "1"); } catch (_) {} return !o; });
  // v3.4.692 — الشهر المختار بالضغط على عمود → معاملات الشهر مجمّعة مشروع ← فئة
  const [selMonth, setSelMonth] = useState(null);
  const [monthDetail, setMonthDetail] = useState(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [openProj, setOpenProj] = useState({});
  useEffect(() => {
    if (!selMonth) { setMonthDetail(null); return; }
    let alive = true; setMonthLoading(true);
    capex.analyzeMonth(selMonth, analysis?.project_id || null).then((r) => { if (alive) { setMonthDetail(r.data); setOpenProj({}); } }).catch(() => { if (alive) setMonthDetail(null); }).finally(() => { if (alive) setMonthLoading(false); });
    return () => { alive = false; };
  }, [selMonth, analysis?.project_id]);
  useEffect(() => { setSelMonth(null); }, [analysis?.project_id]);
  if (loading) {
    return (
      <div style={{ marginTop: 18, padding: 30, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
        ⏳ جاري تحميل التحليل {projectName ? `لمشروع ${projectName}...` : "..."}
      </div>
    );
  }
  if (!analysis || !analysis.series || analysis.series.length === 0) {
    return (
      <div style={{ marginTop: 18, padding: 24, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b", fontSize: 13 }}>
        💡 {projectName ? `لا توجد بيانات تحليل لمشروع ${projectName} بعد — أضف مصاريف لعرض التحليل` : "أضف مصاريف لمشروع لعرض التحليل"}
      </div>
    );
  }

  const data = analysis.series.map(s => {
    const [y, m] = String(s.key).split("-");
    return {
      month: monthLabel(s.key),
      // v3.3.449 — Store mm/yy separately for stacked XAxis rendering
      mm: String(m).padStart(2, "0"),
      yy: y.slice(2),
      "ماضي شهري": Math.round(s.actual),
      "متوقع شهري": Math.round(s.forecast),
      "ماضي تراكمي": Math.round(s.cumulative_actual),
      "متوقع تراكمي": Math.round(s.cumulative_forecast),
      "إجمالي تراكمي": Math.round(s.cumulative_total),
      by_project: s.by_project || {},
    };
  });
  // v3.4.689 — projects that appear in the series, ranked by their total (colour order never changes with filters)
  const canColor = !projectName && Array.isArray(analysis.projects) && analysis.series.some((s) => s.by_project && Object.keys(s.by_project).length);
  const projList = canColor ? analysis.projects.filter((p) => analysis.series.some((s) => s.by_project && s.by_project[p.id])) : [];
  const projColor = {}; projList.forEach((p, i) => { projColor[p.id] = PROJ_COLORS[i % PROJ_COLORS.length]; });
  const colored = byProject && projList.length > 0;
  const cdata = colored ? data.map((d) => { const o = { ...d }; projList.forEach((p) => { const c = d.by_project[p.id] || {}; o[`a_${p.id}`] = c.actual || 0; o[`f_${p.id}`] = c.forecast || 0; }); return o; }) : data;
  const ProjTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    const rows = projList.map((p) => ({ p, a: d[`a_${p.id}`] || 0, f: d[`f_${p.id}`] || 0 })).filter((r) => r.a || r.f);
    return (
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, padding: "8px 10px", maxWidth: 320 }}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>{d.mm}/20{d.yy} — ماضي {fmt(d["ماضي شهري"])}{d["متوقع شهري"] ? ` · متوقع ${fmt(d["متوقع شهري"])}` : ""}</div>
        {rows.map((r) => <div key={r.p.id} style={{ display: "flex", gap: 6, alignItems: "center" }}><span style={{ width: 10, height: 10, borderRadius: 2, background: projColor[r.p.id], flex: "none" }} /><span style={{ flex: 1 }}>{r.p.name}</span><b style={{ direction: "ltr" }}>{fmt(r.a)}</b>{r.f ? <span style={{ color: "#b45309" }}>+{fmt(r.f)} متوقع</span> : null}</div>)}
        <div style={{ color: "#64748b", marginTop: 4 }}>تراكمي ماضي {fmt(d["ماضي تراكمي"])}</div>
      </div>
    );
  };

  // v3.3.449 — Custom XAxis tick: month on top, year on bottom (both 2 digits)
  const StackedDateTick = ({ x, y, payload, index }) => {
    const item = data[index];
    if (!item) return null;
    // Show year label only on first occurrence + Jan changes (to reduce clutter)
    const prevYY = index > 0 ? data[index - 1].yy : null;
    const showYear = index === 0 || item.yy !== prevYY;
    return (
      <g transform={`translate(${x},${y + 4})`}>
        <text x={0} y={0} dy={10} textAnchor="middle" fill="#475569" fontSize={11} fontWeight={600}>
          {item.mm}
        </text>
        {showYear && (
          <text x={0} y={0} dy={26} textAnchor="middle" fill="#94a3b8" fontSize={10} fontWeight={700}>
            {`'${item.yy}`}
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ marginTop: 18, padding: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>📈 تحليل المصاريف</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {projectName ? `مشروع: ${projectName}` : "كل المشاريع"} — ماضي vs متوقع عبر الزمن
          </div>
        </div>
        {canColor && (
          <button type="button" onClick={toggleByProject} title="تلوين أعمدة الأشهر حسب المشروع" style={{ padding: "6px 12px", borderRadius: 8, border: byProject ? "2px solid #2563eb" : "1px solid #cbd5e1", background: byProject ? "#eff6ff" : "#fff", color: byProject ? "#1d4ed8" : "#475569", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            🎨 {byProject ? "✓ " : ""}تلوين حسب المشروع
          </button>
        )}{/* v3.4.689 */}
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={cdata} margin={{ top: 16, right: 30, left: 20, bottom: 20 }} style={{ cursor: "pointer" }}
          onClick={(e) => { const i = e && e.activeTooltipIndex; if (i == null || !analysis.series[i]) return; const k = analysis.series[i].key; setSelMonth((cur) => (cur === k ? null : k)); }}>{/* v3.4.692 */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="month"
            tick={<StackedDateTick />}
            interval={0}
            height={50}
            tickLine={false}
            axisLine={{ stroke: "#cbd5e1" }}
          />
          {/* v3.3.449 — Primary Y axis (left): monthly bars */}
          <YAxis
            yAxisId="monthly"
            orientation="left"
            tick={{ fontSize: 11, fill: "#dc2626" }}
            tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
            width={60}
            label={{ value: "شهري", angle: -90, position: "insideLeft", style: { fill: "#dc2626", fontSize: 11, fontWeight: 700 } }}
          />
          {/* v3.3.449 — Secondary Y axis (right): cumulative lines */}
          <YAxis
            yAxisId="cumulative"
            orientation="right"
            tick={{ fontSize: 11, fill: "#991b1b" }}
            tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
            width={60}
            label={{ value: "تراكمي", angle: 90, position: "insideRight", style: { fill: "#991b1b", fontSize: 11, fontWeight: 700 } }}
          />
          {colored ? <Tooltip content={<ProjTooltip />} /> : <Tooltip formatter={(v) => fmt(v)} contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} labelFormatter={(_, payload) => {
            if (payload && payload[0]) {
              const d = payload[0].payload;
              return `${d.mm}/20${d.yy}`;
            }
            return "";
          }} />}
          {!colored && <Legend wrapperStyle={{ fontSize: 12 }} />}

          {selMonth && (() => { const idx = analysis.series.findIndex((s) => s.key === selMonth); return idx >= 0 && cdata[idx] ? <ReferenceArea yAxisId="monthly" x1={cdata[idx].month} x2={cdata[idx].month} fill="#2563eb" fillOpacity={0.12} stroke="#2563eb" strokeOpacity={0.5} /> : null; })()}{/* v3.4.692 — تظليل الشهر المختار */}
          {colored ? projList.map((p) => [
            <Bar key={`a${p.id}`} yAxisId="monthly" dataKey={`a_${p.id}`} name={p.name} fill={projColor[p.id]} stackId="month" />,
            <Bar key={`f${p.id}`} yAxisId="monthly" dataKey={`f_${p.id}`} name={`${p.name} (متوقع)`} fill={projColor[p.id]} fillOpacity={0.35} stackId="month" />,
          ]) : <>
            <Bar yAxisId="monthly" dataKey="ماضي شهري" fill="#dc2626" stackId="month" />
            <Bar yAxisId="monthly" dataKey="متوقع شهري" fill="#f59e0b" stackId="month" />
          </>}

          <Line yAxisId="cumulative" type="monotone" dataKey="ماضي تراكمي" stroke="#991b1b" strokeWidth={2.5} dot={false} />
          <Line yAxisId="cumulative" type="monotone" dataKey="متوقع تراكمي" stroke="#d97706" strokeWidth={2} strokeDasharray="5 3" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      {colored && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", justifyContent: "center", fontSize: 12, marginTop: 4 }}>
          {projList.map((p) => <span key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: projColor[p.id] }} />{p.name}</span>)}
          <span style={{ color: "#64748b" }}>(اللون الفاتح = متوقع · الخطوط = التراكمي)</span>
        </div>
      )}{/* v3.4.689 */}

      {/* v3.4.692 — معاملات الشهر المختار */}
      {!selMonth && <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 2 }}>اضغط على عمود شهر لعرض معاملاته مجمّعة حسب المشروع وفئة الإنفاق</div>}
      {selMonth && (
        <div style={{ marginTop: 12, border: "1px solid #bfdbfe", background: "#f8fbff", borderRadius: 12, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#1e3a8a" }}>🧾 معاملات شهر {monthLabel(selMonth)}</div>
            {monthDetail && <div style={{ fontSize: 12, color: "#475569" }}>{monthDetail.count} معاملة · ماضي <b style={{ color: "#dc2626" }}>{fmt(monthDetail.total_actual)}</b>{monthDetail.total_forecast ? <> · متوقع <b style={{ color: "#d97706" }}>{fmt(monthDetail.total_forecast)}</b></> : null}</div>}
            {monthLoading && <span style={{ fontSize: 12, color: "#64748b" }}>⏳ جاري التحميل…</span>}
            <div style={{ marginInlineStart: "auto", display: "flex", gap: 6 }}>
              {monthDetail && monthDetail.projects.length > 1 && <button type="button" onClick={() => { const all = monthDetail.projects.every((p) => openProj[p.id] !== false); const o = {}; monthDetail.projects.forEach((p) => { o[p.id] = !all; }); setOpenProj(o); }} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>{monthDetail.projects.every((p) => openProj[p.id] !== false) ? "طيّ الكل" : "توسيع الكل"}</button>}
              <button type="button" onClick={() => setSelMonth(null)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>✕ إغلاق</button>
            </div>
          </div>
          {monthDetail && monthDetail.projects.length === 0 && !monthLoading && <div style={{ color: "#64748b", fontSize: 13, padding: 14, textAlign: "center" }}>لا توجد معاملات في هذا الشهر</div>}
          {monthDetail && monthDetail.projects.map((p) => {
            const open = openProj[p.id] !== false;
            return (
              <div key={p.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                <div onClick={() => setOpenProj((o) => ({ ...o, [p.id]: !open }))} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#eff6ff", cursor: "pointer", fontWeight: 800, fontSize: 13 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: projColor[p.id] || "#64748b" }} />
                  <span>{open ? "▾" : "▸"} {p.name}</span>
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{p.linked ? "قيود المحاسبة" : "مصاريف مسجّلة"} · {p.categories.length} فئة</span>
                  <span style={{ marginInlineStart: "auto", direction: "ltr", fontFamily: "monospace", color: "#dc2626" }}>{fmt(p.actual)}</span>
                  {p.forecast ? <span style={{ direction: "ltr", fontFamily: "monospace", color: "#d97706", fontSize: 12 }}>+{fmt(p.forecast)} متوقع</span> : null}
                </div>
                {open && (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead><tr style={{ background: "#f8fafc", color: "#475569" }}>{["التاريخ", "البيان", "الحساب / مركز التكلفة", "المرجع", "المبلغ"].map((h) => <th key={h} style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700, borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {p.categories.map((c) => (
                        <React.Fragment key={c.category}>
                          <tr style={{ background: "#fffbeb" }}>
                            <td colSpan={4} style={{ padding: "6px 10px", fontWeight: 800, color: "#92400e", borderBottom: "1px solid #fde68a" }}>📂 {c.category} <span style={{ fontWeight: 500, color: "#a16207", fontSize: 11 }}>({c.rows.length})</span></td>
                            <td style={{ padding: "6px 10px", fontWeight: 800, direction: "ltr", textAlign: "right", fontFamily: "monospace", color: "#92400e", borderBottom: "1px solid #fde68a" }}>{fmt(c.actual)}{c.forecast ? <span style={{ color: "#d97706", fontWeight: 500 }}> +{fmt(c.forecast)}</span> : null}</td>
                          </tr>
                          {c.rows.map((r) => (
                            <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9", color: r.forecast ? "#b45309" : "#1e293b", background: r.forecast ? "#fffdf5" : "#fff" }}>
                              <td style={{ padding: "5px 10px", whiteSpace: "nowrap", direction: "ltr", textAlign: "right", fontFamily: "monospace" }}>{String(r.date || "").slice(0, 10)}</td>
                              <td style={{ padding: "5px 10px", maxWidth: 420 }}>{r.forecast ? "🟡 متوقع — " : ""}{r.description || "—"}</td>
                              <td style={{ padding: "5px 10px", fontSize: 11, color: "#475569" }}>{[r.account, r.cost_center].filter(Boolean).join(" · ") || "—"}</td>
                              <td style={{ padding: "5px 10px", fontSize: 11, color: "#64748b", direction: "ltr", textAlign: "right" }}>{r.ref || "—"}</td>
                              <td style={{ padding: "5px 10px", direction: "ltr", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: r.amount < 0 ? "#16a34a" : undefined }}>{fmt(r.amount)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary boxes */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 14 }}>
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>إجمالي ماضي 🔴</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#dc2626", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(analysis.total_actual)}</div>
        </div>
        <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>إجمالي متوقع 🟡</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#d97706", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(analysis.total_forecast)}</div>
        </div>
        <div style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 600 }}>الإجمالي الكلي</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#2563eb", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(analysis.total_actual + analysis.total_forecast)}</div>
        </div>
        <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 14px" }}>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>الأشهر</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", direction: "ltr" }}>{analysis.series.length}</div>
        </div>
      </div>

      {/* v3.3.392 — Category breakdown bars */}
      {analysis.categories && analysis.categories.length > 0 && (
        <CategoryBreakdownChart categories={analysis.categories} />
      )}
    </div>
  );
}

// ─── Category breakdown horizontal bars ───
function CategoryBreakdownChart({ categories }) {
  // Map server categories to display config
  const total = categories.reduce((s, c) => s + c.total, 0);
  if (total === 0) return null;

  // Sort by CAPEX_CATEGORIES order, plus any "other" at the end
  const ordered = CAPEX_CATEGORIES.map(cfg => {
    const data = categories.find(c => c.category === cfg.key) || { actual: 0, forecast: 0, total: 0 };
    return { ...cfg, ...data, pct: total > 0 ? (data.total / total) * 100 : 0 };
  }).filter(c => c.total > 0);

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px dashed #e2e8f0" }}>
      <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", marginBottom: 10 }}>
        🧮 توزيع حسب الفئة
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {ordered.map(c => (
          <div key={c.key}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ fontWeight: 700, color: c.color }}>
                {c.icon} {c.label} <span style={{ fontWeight: 500, opacity: 0.7 }}>({c.pct.toFixed(1)}%)</span>
              </span>
              <span style={{ direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: c.color }}>
                {fmt(c.total)}
              </span>
            </div>
            <div style={{ position: "relative", height: 16, background: "#f1f5f9", borderRadius: 4, overflow: "hidden", display: "flex" }}>
              {c.actual > 0 && (
                <div style={{ width: `${(c.actual / total) * 100}%`, background: "#dc2626" }} title={`ماضي: ${fmt(c.actual)}`} />
              )}
              {c.forecast > 0 && (
                <div style={{ width: `${(c.forecast / total) * 100}%`, background: "#f59e0b" }} title={`متوقع: ${fmt(c.forecast)}`} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Project Breakdown Table (only in "all projects" view)
// ═══════════════════════════════════════════════════════════════════
function ProjectBreakdownTable({ projects }) {
  const totalAll = projects.reduce((s, p) => s + p.total, 0);
  return (
    <div style={{ marginTop: 16, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
        🧮 تفصيل لكل مشروع
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f1f5f9" }}>
            <th style={{ padding: "8px 10px", textAlign: "right", color: "#475569", fontWeight: 700 }}>المشروع</th>
            <th style={{ padding: "8px 10px", textAlign: "left", color: "#475569", fontWeight: 700 }}>الميزانية</th>
            <th style={{ padding: "8px 10px", textAlign: "left", color: "#dc2626", fontWeight: 700 }}>ماضي</th>
            <th style={{ padding: "8px 10px", textAlign: "left", color: "#d97706", fontWeight: 700 }}>متوقع</th>
            <th style={{ padding: "8px 10px", textAlign: "left", color: "#0f172a", fontWeight: 700 }}>الإجمالي</th>
            <th style={{ padding: "8px 10px", textAlign: "center", color: "#475569", fontWeight: 700 }}>%</th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => {
            const pct = totalAll > 0 ? (p.total / totalAll) * 100 : 0;
            return (
              <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px 10px", fontWeight: 700 }}>{p.name}</td>
                <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{fmt(p.total_budget)}</td>
                <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#dc2626", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(p.actual)}</td>
                <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#d97706", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(p.forecast)}</td>
                <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#0f172a", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmt(p.total)}</td>
                <td style={{ padding: "8px 10px", textAlign: "center", color: "#64748b" }}>{pct.toFixed(1)}%</td>
              </tr>
            );
          })}
          <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
            <td style={{ padding: "8px 10px" }}>الإجمالي</td>
            <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr" }}>{fmt(projects.reduce((s, p) => s + p.total_budget, 0))}</td>
            <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#dc2626" }}>{fmt(projects.reduce((s, p) => s + p.actual, 0))}</td>
            <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr", color: "#d97706" }}>{fmt(projects.reduce((s, p) => s + p.forecast, 0))}</td>
            <td style={{ padding: "8px 10px", textAlign: "left", direction: "ltr" }}>{fmt(totalAll)}</td>
            <td style={{ padding: "8px 10px", textAlign: "center" }}>100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ImportModal — رفع ملف Excel لاستيراد مشروع كامل (v3.3.394)
// ═══════════════════════════════════════════════════════════════════
function ImportModal({ projects, selectedProjectId, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState(selectedProjectId ? "current" : "new"); // "new" | "current" | "existing"
  const [targetExistingId, setTargetExistingId] = useState(selectedProjectId || "");
  const [mode, setMode] = useState("replace"); // when target != "new"
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().match(/\.(xlsx|xls|xlsm)$/)) {
      toast.error("الملف يجب أن يكون Excel (.xlsx)");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("اختر ملفاً أولاً"); return; }
    setUploading(true);
    try {
      let pid = null;
      if (target === "current" && selectedProjectId) pid = selectedProjectId;
      else if (target === "existing" && targetExistingId) pid = parseInt(targetExistingId);
      // pid stays null if target === "new"

      const r = await capex.importFile(file, pid, pid ? mode : null);
      const data = r.data;
      setResult(data);
      toast.success(`${data.action} المشروع "${data.project_name}" — ${data.inserted} حركة`);
      // Notify parent so it refreshes & navigates
      setTimeout(() => onImported(data.project_id), 800);
    } catch (e) {
      toast.error(e.response?.data?.error || "فشل الاستيراد");
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
              <FiUpload size={18} style={{ color: "#7c3aed" }} /> استيراد من Excel
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>ارفع ملف XLSX يحتوي على بيانات المشروع والمصاريف</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: "#64748b" }}>✕</button>
        </div>

        <div style={{ padding: 18 }}>
          {/* Template download tip */}
          <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: "#92400e" }}>
              💡 لم يكن لديك ملف؟ حمّل القالب الفارغ مع تعليمات وأمثلة
            </div>
            <button
              onClick={async () => {
                try {
                  await capex.downloadTemplate();
                  toast.success("تم تحميل القالب");
                } catch (e) {
                  toast.error(e.response?.data?.error || "فشل التحميل");
                }
              }}
              style={{ padding: "5px 12px", background: "#d97706", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <FiDownload size={11} /> تحميل القالب
            </button>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            style={{
              border: file ? "2px solid #16a34a" : "2px dashed #cbd5e1",
              background: file ? "#f0fdf4" : "#f8fafc",
              borderRadius: 10, padding: 22, textAlign: "center", marginBottom: 14, cursor: "pointer",
            }}
            onClick={() => document.getElementById("capex-import-file").click()}
          >
            <input
              id="capex-import-file"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {file ? (
              <div>
                <FiFile size={28} style={{ color: "#16a34a", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#15803d", fontSize: 13 }}>{file.name}</div>
                <div style={{ fontSize: 11, color: "#166534", marginTop: 3 }}>{(file.size / 1024).toFixed(1)} KB · اضغط للتغيير</div>
              </div>
            ) : (
              <div>
                <FiUpload size={28} style={{ color: "#94a3b8", marginBottom: 6 }} />
                <div style={{ fontWeight: 700, color: "#475569", fontSize: 13 }}>اسحب الملف هنا أو اضغط للاختيار</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>XLSX, XLS, XLSM (حد أقصى 10MB)</div>
              </div>
            )}
          </div>

          {/* Target selection */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>📍 وجهة الاستيراد:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", background: target === "new" ? "#eff6ff" : "transparent", borderRadius: 6, border: target === "new" ? "1px solid #93c5fd" : "1px solid #e2e8f0" }}>
                <input type="radio" checked={target === "new"} onChange={() => setTarget("new")} />
                <span style={{ fontSize: 12, color: "#0f172a" }}>🆕 إنشاء مشروع جديد (بالاسم الموجود في الملف)</span>
              </label>
              {selectedProjectId && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", background: target === "current" ? "#eff6ff" : "transparent", borderRadius: 6, border: target === "current" ? "1px solid #93c5fd" : "1px solid #e2e8f0" }}>
                  <input type="radio" checked={target === "current"} onChange={() => setTarget("current")} />
                  <span style={{ fontSize: 12, color: "#0f172a" }}>♻️ تحديث المشروع الحالي ({projects.find(p => p.id === selectedProjectId)?.name})</span>
                </label>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", background: target === "existing" ? "#eff6ff" : "transparent", borderRadius: 6, border: target === "existing" ? "1px solid #93c5fd" : "1px solid #e2e8f0" }}>
                <input type="radio" checked={target === "existing"} onChange={() => setTarget("existing")} />
                <span style={{ fontSize: 12, color: "#0f172a" }}>🎯 تحديث مشروع آخر:</span>
                <select
                  value={targetExistingId}
                  disabled={target !== "existing"}
                  onChange={(e) => setTargetExistingId(e.target.value)}
                  style={{ padding: "3px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1", fontFamily: "inherit", flex: 1 }}
                >
                  <option value="">اختر...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Mode (only for existing projects) */}
          {target !== "new" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>⚙️ كيفية معالجة المصاريف الحالية:</div>
              <div style={{ display: "flex", gap: 8 }}>
                <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 10px", background: mode === "replace" ? "#fef2f2" : "transparent", borderRadius: 6, border: mode === "replace" ? "1px solid #fca5a5" : "1px solid #e2e8f0" }}>
                  <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b" }}>🗑️ استبدال</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>حذف القديم وإحلال الجديد</div>
                  </div>
                </label>
                <label style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "8px 10px", background: mode === "append" ? "#f0fdf4" : "transparent", borderRadius: 6, border: mode === "append" ? "1px solid #86efac" : "1px solid #e2e8f0" }}>
                  <input type="radio" checked={mode === "append"} onChange={() => setMode("append")} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>➕ إضافة</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>الاحتفاظ بالقديم وإضافة الجديد</div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Result preview */}
          {result && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                ✓ {result.action} "{result.project_name}" — {result.inserted} حركة
              </div>
              {result.errors && result.errors.length > 0 && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ fontSize: 11, color: "#dc2626", cursor: "pointer", fontWeight: 700 }}>{result.errors.length} تحذيرات</summary>
                  <ul style={{ fontSize: 11, color: "#dc2626", margin: "6px 0 0 18px" }}>
                    {result.errors.slice(0, 10).map((er, i) => <li key={i}>{er}</li>)}
                    {result.errors.length > 10 && <li>... و {result.errors.length - 10} أخرى</li>}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={uploading} style={{ padding: "8px 18px", background: "#e2e8f0", color: "#475569", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer" }}>
              إلغاء
            </button>
            <button
              onClick={submit}
              disabled={!file || uploading || (target === "existing" && !targetExistingId)}
              style={{
                padding: "8px 22px",
                background: (!file || uploading || (target === "existing" && !targetExistingId)) ? "#cbd5e1" : "#7c3aed",
                color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700,
                cursor: (!file || uploading || (target === "existing" && !targetExistingId)) ? "not-allowed" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {uploading ? "جاري الرفع..." : <><FiUpload size={12} /> استيراد</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.628 — AccountingLinkSection — ربط المشروع بشجرة التكاليف المحاسبية
// يختار المستخدم الفرع الرئيسي للمشروع في الشجرة، ثم يُجلب كامل الشجرة الفرعية
// مع الأرقام الفعلية (مجمّعة من حركات Daftra) + تفاصيل العمليات لكل مركز تكلفة.
// ═══════════════════════════════════════════════════════════════════
function AccountingLinkSection({ projectId, project, canEdit, onLinked }) {
  const [data, setData] = useState(null);        // { linked, branch, nodes[], rollup }
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const linkedNodeId = project?.cc_tree_node_id || null;

  const load = () => {
    if (!linkedNodeId) { setData({ linked: false, nodes: [] }); return; }
    setLoading(true);
    capex.projectAccounting(projectId)
      .then(r => setData(r.data))
      .catch(() => toast.error("فشل تحميل البيانات المحاسبية"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [projectId, linkedNodeId]);

  const saveLink = async (nodeId) => {
    try {
      await capex.updateProject(projectId, { cc_tree_node_id: nodeId });
      toast.success(nodeId ? "تم الربط" : "تم إلغاء الربط");
      setShowPicker(false);
      onLinked && onLinked();
    } catch { toast.error("فشل الحفظ"); }
  };

  // v3.3.629 — بناء شجرة متداخلة + احتساب الإجمالي التراكمي لكل عقدة (المجموعات تجمع أبناءها)
  // الأقسام الرئيسية = أبناء الفرع الجذر مباشرةً، وتُعرض كبطاقات بنفس فورمات أقسام المصاريف.
  const cards = useMemo(() => {
    if (!data?.nodes?.length) return [];
    const byId = {};
    data.nodes.forEach(n => { byId[n.id] = { ...n, children: [] }; });
    data.nodes.forEach(n => { if (n.parent_id != null && byId[n.parent_id]) byId[n.parent_id].children.push(byId[n.id]); });
    const computeRoll = (node) => {
      let net = Number(node.net) || 0, tx = node.tx_count || 0;
      for (const c of node.children) { const r = computeRoll(c); net += r.net; tx += r.tx; }
      node.rollNet = net; node.rollTx = tx;
      return { net, tx };
    };
    const root = byId[data.branch?.id];
    if (root) computeRoll(root);
    return root ? root.children : [];
  }, [data]);

  return (
    <div style={{ marginTop: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
          🌳 الربط بشجرة التكاليف المحاسبية
          <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>(الفعلي من المحاسبة)</span>
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {linkedNodeId
            ? <span style={{ fontSize: 12, color: "#15803d", fontWeight: 700 }}>مربوط بـ: {data?.branch?.name || `#${linkedNodeId}`}</span>
            : <span style={{ fontSize: 12, color: "#b45309" }}>غير مربوط</span>}
          {canEdit && (
            <button onClick={() => setShowPicker(true)}
              style={{ padding: "6px 12px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              {linkedNodeId ? "تغيير الفرع" : "ربط بفرع"}
            </button>
          )}
          {canEdit && linkedNodeId && (
            <button onClick={() => saveLink(null)}
              style={{ padding: "6px 10px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              إلغاء الربط
            </button>
          )}
        </div>
      </div>

      {!linkedNodeId ? (
        <div style={{ fontSize: 12, color: "#94a3b8", padding: "10px 0" }}>اختر الفرع الرئيسي للمشروع في شجرة التكاليف ليتم استجلاب كامل فروعه وأرقامه الفعلية.</div>
      ) : loading ? (
        <div style={{ fontSize: 12, color: "#94a3b8", padding: 10 }}>جاري التحميل...</div>
      ) : (
        <>
          {data?.rollup && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 8, padding: "8px 14px" }}>
                <div style={{ fontSize: 10, color: "#0e7490", fontWeight: 700 }}>إجمالي الفعلي (مدين - دائن)</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#0891b2", fontFamily: "monospace" }}>{fmt(data.rollup.net)} ر.س</div>
              </div>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px" }}>
                <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>عدد الحركات</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#334155", fontFamily: "monospace" }}>{fmt(data.rollup.tx_count)}</div>
              </div>
            </div>
          )}
          {cards.length === 0
            ? <div style={{ fontSize: 12, color: "#94a3b8" }}>لا توجد فروع تحت هذا الفرع.</div>
            : cards.map((c, i) => (
                <LinkedBranchCard key={c.id} node={c} palette={LINKED_PALETTE[i % LINKED_PALETTE.length]} />
              ))}
        </>
      )}

      {showPicker && (
        <TreePickerModal currentId={linkedNodeId} onPick={saveLink} onClose={() => setShowPicker(false)} />
      )}
    </div>
  );
}

// v3.3.629 — لوحة ألوان للأقسام الرئيسية المربوطة (بنفس روح بطاقات المصاريف)
const LINKED_PALETTE = [
  { color: "#15803d", bg: "#dcfce7", border: "#86efac" },
  { color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
  { color: "#0e7490", bg: "#cffafe", border: "#a5f3fc" },
  { color: "#9333ea", bg: "#f3e8ff", border: "#d8b4fe" },
  { color: "#be185d", bg: "#fce7f3", border: "#f9a8d4" },
  { color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
  { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
];

// v3.3.629/630 — بطاقة قسم رئيسي مربوط (نفس فورمات بطاقات المصاريف الرأسمالية)
// قابلة للتوسعة: المجموعات تُظهر الفروع، والأوراق (مراكز التكلفة) تُظهر العمليات تحتها مباشرة.
function LinkedBranchCard({ node, palette }) {
  const [collapsed, setCollapsed] = useState(true);
  const hasKids = node.children && node.children.length > 0;
  const isLeaf = node.node_type === "leaf";
  const expandable = hasKids || (isLeaf && node.tx_count > 0);
  return (
    <div style={{ marginBottom: 12, background: "#fff", border: `1.5px solid ${palette.border}`, borderRadius: 10, overflow: "hidden" }}>
      <div onClick={() => expandable && setCollapsed(c => !c)}
        style={{ padding: "10px 14px", background: palette.bg, borderBottom: collapsed ? "none" : `1px solid ${palette.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", cursor: expandable ? "pointer" : "default" }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: palette.color }}>
            {isLeaf ? "📄" : "📁"} {node.name}
            {node.ref_no && <span style={{ fontSize: 10, fontWeight: 600, marginRight: 8, opacity: 0.7 }}>[{node.ref_no}]</span>}
          </div>
          <div style={{ fontSize: 11, color: palette.color, marginTop: 2, opacity: 0.85 }}>{fmt(node.rollTx)} حركة</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: palette.color, direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(node.rollNet)}</span>
          {expandable && <span style={{ color: palette.color, fontSize: 14 }}>{collapsed ? "▽" : "△"}</span>}
        </div>
      </div>
      {!collapsed && (
        <div>
          {hasKids
            ? node.children.map(c => <TreeNodeRow key={c.id} node={c} depth={1} />)
            : (isLeaf && node.tx_count > 0 ? <OperationsList node={node} depth={1} /> : null)}
        </div>
      )}
    </div>
  );
}

// صف عقدة فرعية — كل المستويات قابلة للطي/التوسعة؛ العمليات تظهر تحت الورقة مباشرة
function TreeNodeRow({ node, depth }) {
  const [open, setOpen] = useState(false);
  const isLeaf = node.node_type === "leaf";
  const hasKids = node.children && node.children.length > 0;
  const expandable = hasKids || (isLeaf && node.tx_count > 0);
  return (
    <>
      <div onClick={() => expandable && setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
        paddingRight: 10 + depth * 18, borderBottom: "1px solid #f8fafc", background: "#fff",
        cursor: expandable ? "pointer" : "default",
      }}>
        <span style={{ width: 14, color: "#94a3b8", fontSize: 11 }}>{expandable ? (open ? "▼" : "◀") : ""}</span>
        <span style={{ flex: 1, fontSize: 12, fontWeight: isLeaf ? 500 : 700, color: "#0f172a" }}>
          {isLeaf ? "📄" : "📁"} {node.name}
          {node.ref_no && <span style={{ fontSize: 9, color: "#94a3b8", marginRight: 6 }}>[{node.ref_no}]</span>}
        </span>
        {isLeaf && node.tx_count > 0 && (
          <span style={{ fontSize: 9, background: "#eef2ff", color: "#4338ca", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>{node.tx_count} عملية</span>
        )}
        <span style={{ fontSize: 12, fontFamily: "monospace", fontWeight: 700, color: node.rollNet < 0 ? "#dc2626" : "#0891b2", minWidth: 90, textAlign: "left" }}>
          {fmt(node.rollNet)}
        </span>
      </div>
      {open && (hasKids
        ? node.children.map(c => <TreeNodeRow key={c.id} node={c} depth={depth + 1} />)
        : (isLeaf && node.tx_count > 0 ? <OperationsList node={node} depth={depth + 1} /> : null))}
    </>
  );
}

// v3.3.630 — قائمة العمليات (الحركات) تُحمَّل عند توسعة الورقة وتظهر تحتها مباشرة
function OperationsList({ node, depth = 1 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const params = { with_attachments: 1 }; // v3.3.632 — يجلب مرفقات الفاتورة من Daftra
    if (node.cost_center_id) params.cost_center_id = node.cost_center_id;
    else if (node.journal_account_id) params.journal_account_id = node.journal_account_id;
    capex.ccTransactions(params).then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [node]);
  const pad = 10 + depth * 18 + 14;
  if (items === null) return <div style={{ padding: "6px 10px", paddingRight: pad, fontSize: 11, color: "#94a3b8", background: "#fafafa" }}>جاري تحميل العمليات...</div>;
  if (items.length === 0) return <div style={{ padding: "6px 10px", paddingRight: pad, fontSize: 11, color: "#94a3b8", background: "#fafafa" }}>لا توجد عمليات.</div>;
  return (
    <div style={{ background: "#fafafa" }}>
      {items.map(t => (
        <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 10px", paddingRight: pad, borderBottom: "1px solid #f1f5f9", fontSize: 11 }}>
          <span style={{ fontFamily: "monospace", color: "#475569", width: 80, flexShrink: 0 }}>{fmtDateDMY(t.journal_date)}</span>
          <span style={{ flex: 1, color: "#334155" }}>
            {t.journal_description || t.journal_account_name || "—"}
            {t.journal_number ? <span style={{ color: "#94a3b8" }}> · #{t.journal_number}</span> : ""}
          </span>
          {/* v3.3.636 — الفاتورة: يفتح صفحة القيد في دفتر (بجلسة المستخدم) حيث تظهر الفاتورة */}
          {t.attachments && t.attachments.length > 0 && (
            <button
              type="button"
              title={`افتح القيد في دفتر لعرض الفاتورة: ${t.attachments.map(a => a.name).filter(Boolean).join("، ")}`}
              onClick={(e) => { e.stopPropagation(); if (t.journal_web_url) window.open(t.journal_web_url, "_blank", "noopener"); }}
              style={{ fontSize: 11, background: "#ecfeff", color: "#0e7490", border: "1px solid #a5f3fc", borderRadius: 8, padding: "2px 8px", flexShrink: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
              📎 {t.attachments.length > 1 ? `${t.attachments.length} فواتير` : "فاتورة"}
            </button>
          )}
          <span style={{ fontFamily: "monospace", fontWeight: 700, color: Number(t.debit) ? "#0891b2" : "#dc2626", minWidth: 80, textAlign: "left" }}>
            {Number(t.debit) ? fmt(t.debit) : `(${fmt(t.credit)})`}
          </span>
        </div>
      ))}
    </div>
  );
}

// منتقي الفرع الرئيسي من شجرة التكاليف
function TreePickerModal({ currentId, onPick, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    capex.ccTree().then(r => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  const tree = useMemo(() => {
    if (!items) return [];
    const byParent = {};
    items.forEach(n => { (byParent[n.parent_id] = byParent[n.parent_id] || []).push(n); });
    const build = (id) => (byParent[id] || []).map(n => ({ ...n, children: build(n.id) }));
    return build(null);
  }, [items]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 18, width: "min(560px, 95vw)", maxHeight: "85vh", overflow: "auto", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>اختر الفرع الرئيسي للمشروع</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>×</button>
        </div>
        {items === null ? <div style={{ color: "#94a3b8", fontSize: 12 }}>جاري التحميل...</div>
          : items.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 12 }}>لا توجد شجرة تكاليف. أنشئها أولاً في البرنامج المحاسبي.</div>
          : <PickerNodes nodes={tree} depth={0} currentId={currentId} onPick={onPick} />}
      </div>
    </div>
  );
}

function PickerNodes({ nodes, depth, currentId, onPick }) {
  return nodes.map(n => (
    <div key={n.id}>
      <div onClick={() => onPick(n.id)} style={{
        cursor: "pointer", padding: "6px 8px", paddingRight: 8 + depth * 16,
        fontSize: 12, borderRadius: 6, color: n.id === currentId ? "#0e7490" : "#0f172a",
        background: n.id === currentId ? "#cffafe" : "transparent", fontWeight: n.node_type === "leaf" ? 500 : 700,
      }}
      onMouseEnter={e => { if (n.id !== currentId) e.currentTarget.style.background = "#f1f5f9"; }}
      onMouseLeave={e => { if (n.id !== currentId) e.currentTarget.style.background = "transparent"; }}>
        {n.node_type === "leaf" ? "📄" : "📁"} {n.name}{n.ref_no ? ` [${n.ref_no}]` : ""}
      </div>
      {n.children && n.children.length > 0 && <PickerNodes nodes={n.children} depth={depth + 1} currentId={currentId} onPick={onPick} />}
    </div>
  ));
}

// (v3.3.630 — استُبدل TransactionsModal بقائمة العمليات المضمّنة OperationsList أعلاه)
